"use client";
import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
} from "react";

import BaseInput, { BaseInputProps } from "@/ui/Forms/BaseInput";

export interface FormNumberInputProps extends BaseInputProps {
  unit?: string;
  onValueChange?: (value: number | null) => void;
  allowNegative?: boolean;
  allowDecimals?: boolean;
  decimal?: boolean;
  maxDecimals?: number;
}

const FormNumberInputComponent = (
  {
    unit,
    onValueChange,
    allowNegative = false,
    allowDecimals = true,
    decimal,
    maxDecimals = 2,
    onChange,
    value,
    ...props
  }: FormNumberInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const shouldAllowDecimals = decimal !== undefined ? decimal : allowDecimals;

  // Обработка значения value для предотвращения переключения из неконтролируемого в контролируемый режим
  const normalizedValue =
    value === undefined || value === null ? "" : String(value);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (value === "") {
        onValueChange?.(null);
        onChange?.(event);
        return;
      }

      const regex = shouldAllowDecimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
      if (!regex.test(value)) {
        return;
      }

      if (!allowNegative && value.startsWith("-")) {
        return;
      }

      if (shouldAllowDecimals) {
        const parts = value.split(".");
        if (parts[1] && parts[1].length > maxDecimals) {
          return;
        }
      }

      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        onValueChange?.(numericValue);
      } else {
        onValueChange?.(null);
      }

      onChange?.(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // All props excluded - number input validation logic optimized for performance
  );

  return (
    <BaseInput
      ref={ref}
      type="text"
      inputMode="decimal"
      value={normalizedValue}
      onChange={handleChange}
      insideRightComponent={
        unit ? <div className="form-number-input-unit">{unit}</div> : undefined
      }
      {...props}
    />
  );
};

const FormNumberInput = memo(forwardRef(FormNumberInputComponent));

// Добавляем displayName для отладки
FormNumberInput.displayName = "FormNumberInput";

export default FormNumberInput;
